import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { BackendService } from "./backend.service";
import { AuthService } from "./auth.service";

/**
 * Tracks how many document requests (across every linked patient) are still
 * pending the patient's action, so the sidebar can badge the Documents nav item.
 *
 * The count is derived from the existing documents list endpoint (no dedicated
 * count endpoint) — `refresh()` re-fetches and republishes, and the documents
 * page pushes its already-loaded count via `setPendingCount()` to avoid a second
 * round-trip.
 */
@Injectable({ providedIn: "root" })
export class DocumentsNotificationService {
  private readonly _pendingCount = new BehaviorSubject<number>(0);
  /** Number of document requests still awaiting review/signature/upload. */
  readonly pendingCount$: Observable<number> =
    this._pendingCount.asObservable();

  constructor(
    private authService: AuthService,
    private backendService: BackendService,
  ) {}

  /** Publish a pending count computed elsewhere (e.g. the documents page list). */
  setPendingCount(count: number): void {
    this._pendingCount.next(Math.max(0, count || 0));
  }

  /** Re-fetch document requests and publish the pending-review count. */
  async refresh(): Promise<void> {
    let token: string;
    try {
      token = await this.authService.getAuthTokenOnce();
    } catch {
      return; // not authenticated yet — leave the current count untouched
    }
    if (!token) return;
    this.backendService.getDocumentRequests(token).subscribe({
      next: (response) => {
        const pending = (response?.requests || []).filter(
          (r) => r.status === "pending",
        ).length;
        this._pendingCount.next(pending);
      },
      // Transient failure: keep the last known count rather than clearing it.
      error: () => {},
    });
  }
}
